import { useEffect, useState } from 'react';
import {
  FsHeading,
  FsParagraph,
  FsToast,
  HeadingTag,
  ToastType,
} from '@printi/ds-offset-react-core';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import registerImage from '@/assets/register-and-enjoy.png';

const NewsletterSection = styled.section``;

const NewsletterGrid = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .newsletter-image {
    display: flex;
    flex-direction: column;
  }
`;

const NewsletterForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3x);
  padding: var(--spacing-5x);
  border: 1px solid var(--elements-border-color);
  margin-top: calc(var(--spacing-8x) - 3px);
  height: 403px;

  @media (max-width: 768px) {
    margin-top: 0;
    padding: var(--spacing-4x);
    height: auto;
  }

  .privacy-policy {
    color: rgb(82, 82, 82);
    font-size: 14px;
    line-height: 20px;
    font-family: var(--font-family-base);
    font-weight: var(--font-weight-regular);

    a {
      text-decoration: underline;
    }
  }
`;

const HubspotFormContainer = styled.div`
  height: 100%;

  .hs-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-3x);
  }

  // Reset HubSpot default styles
  .hs-form-field {
    margin-bottom: 0;

    &.hs-error {
      > label {
        color: var(--negative-placeholder-color);
      }

      .hs-input {
        border-color: var(--negative-placeholder-color);
        color: var(--negative-placeholder-color);
      }

      .hs_email input.hs-input::placeholder,
      .hs_email input.hs-input::-webkit-input-placeholder,
      .hs_email input.hs-input::-moz-placeholder,
      .hs_email input.hs-input:-ms-input-placeholder,
      .hs_email input.hs-input:-moz-placeholder {
        color: var(--negative-placeholder-color) !important;
        opacity: 1 !important;
      }

      select.hs-input {
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23D92D20' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      }
    }
  }

  // Estilo específico para o placeholder do email em estado de erro
  .hs_email.hs-error input[type='email']::placeholder,
  .hs_email.hs-error input[type='email']::-webkit-input-placeholder,
  .hs_email.hs-error input[type='email']::-moz-placeholder,
  .hs_email.hs-error input[type='email']:-ms-input-placeholder,
  .hs_email.hs-error input[type='email']:-moz-placeholder {
    color: var(--negative-placeholder-color) !important;
    opacity: 1 !important;
  }

  .hs-form-field > label {
    font: var(--_label-md-2-font);
    letter-spacing: var(--_label-md-2-letter-spacing);
    color: var(--text-label-color);
    margin: var(--spacing-1x) 0;
    display: block;
  }

  .hs-form-required {
    display: none;
  }

  .hs_finalidade > label > span {
    visibility: hidden;
    position: relative;
    content: '';
  }

  .hs_finalidade > label > span::after {
    visibility: visible;
    display: block;
    content: 'Para qual finalidade você busca nossos produtos?';
  }

  .hs-finalidade {
    margin-top: -20px;
  }

  .hs-input {
    font: var(--_placeholder-sm-font);
    letter-spacing: var(--_placeholder-letter-spacing);
    color: var(--text-placeholder-color);
    width: 100%;
    outline: none;
    border: var(--default-border-width) solid var(--elements-border-color);
    box-sizing: border-box;
    padding-left: var(--spacing-2x);
    padding-right: var(--spacing-2x);
    height: var(--size-8x);
    border-radius: var(--default-border-radius);
    transition-property: background-color, opacity, border-color, color;
    transition-timing-function: var(--curve-productive-exit);
    transition-duration: var(--duration-fast-01);

    &:focus {
      border-color: var(--action-bg-primary-color);
    }

    &::placeholder {
      color: var(--text-placeholder-color);
      opacity: 1;
    }
  }

  select.hs-input {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23525252' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right var(--spacing-2x) center;
    background-size: 24px;
  }

  // Error states
  .hs-error-msgs {
    list-style: none;
    padding: 0;
    margin: var(--spacing-1x) 0 0 0;

    label {
      font: var(--_body-sm-font);
      letter-spacing: var(--_body-sm-letter-spacing);
      color: var(--feedback-error-color);
      margin: 0;
    }
  }

  .inputs-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  input.hs-button {
    font: var(--_label-md-2-font);
    letter-spacing: var(--_label-md-2-letter-spacing);
    width: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--on-action-label-primary-color);
    background-color: var(--action-bg-primary-color);
    border: 0px;
    border-radius: var(--border-radius-circular);
    cursor: pointer;
    box-sizing: border-box;
    transition-property: background-color, opacity;
    transition-timing-function: var(--curve-productive-exit);
    transition-duration: var(--duration-fast-02);
    height: var(--size-6x);
    min-width: 132px;
    padding: 0 var(--spacing-4x);
  }

  .hs_error_rollup {
    display: none;
  }

  .hs-input.invalid.error {
    color: var(--negative-placeholder-color);
    border-color: var(--negative-placeholder-color);
    border-radius: var(--default-border-radius);
  }

  ul.hs-error-msgs {
    margin-top: var(--spacing-1x);
  }

  ul.hs-error-msgs label.hs-error-msg {
    font: var(--_support-text-font);
    letter-spacing: var(--_support-text-letter-spacing);
    color: var(--negative-support-text-color);
  }

  .submitted-message {
    font-size: 32px;
    font-family: var(--font-family-base);
    font-weight: var(--font-weight-regular);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:not(:empty) {
      font-size: 0;
    }

    &:not(:empty)::after {
      content: 'Obrigado por se cadastrar!';
      font-size: 32px;
      display: block;
    }
  }

  &.form-submitted + .privacy-policy {
    display: none;
  }
`;

type ToastState = {
  opened: boolean;
  description: string;
  type: ToastType;
};

interface FormField {
  name: string;
  value: string;
}

interface HubSpotFormV2 {
  getFields: () => FormField[];
  addFieldError: (fieldName: string, message: string) => void;
}

interface HubSpotFormConfig {
  portalId: string;
  formId: string;
  region: string;
  target: string;
  onFormSubmit?: (form: HubSpotFormV2) => boolean | void;
  onFormSubmitted?: () => void;
  onFormReady?: (form: HubSpotFormV2) => void;
  locale?: string;
  fields?: {
    name: string;
    label?: string;
    placeholder?: string;
  }[];
  translations?: {
    [locale: string]: {
      submitText?: string;
      required?: string;
      invalidEmail?: string;
      invalidEmailFormat?: string;
      missingRequiredField?: string;
    };
  };
}

interface HubSpotWindow {
  hbspt?: {
    forms: {
      create: (config: HubSpotFormConfig) => void;
    };
  };
}

export function HomeV2Newsletter() {
  const [toast, setToast] = useState<ToastState>({
    opened: false,
    description: '',
    type: ToastType.Positive,
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//js.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    document.body.appendChild(script);

    script.onload = () => {
      const hubspot = (window as unknown as HubSpotWindow).hbspt;
      if (hubspot?.forms?.create) {
        const config: HubSpotFormConfig = {
          portalId: '44103013', // production
          formId: '5a32ae03-d73a-44a3-b91b-b75e0a416c26', // production
          // portalId: '44676088', // staging
          // formId: '6d7477d4-64be-4f60-8970-2fa4ac895eed', // staging
          region: 'na1',
          target: '#hubspot-form-container',
          locale: 'pt-br',
          translations: {
            'pt-br': {
              submitText: 'Cadastrar',
              required: 'Preencha esse campo obrigatório.',
              invalidEmail: 'E-mail não preenchido ou inválido',
              invalidEmailFormat: 'E-mail não preenchido ou inválido',
              missingRequiredField: 'Selecione uma opção',
            },
          },
          fields: [
            {
              name: 'email',
              placeholder: 'Ex.: maria.silva@printi.com.br',
            },
          ],
          onFormReady: () => {
            const setPlaceholder = () => {
              const emailInput = document.querySelector(
                '.hs_email input[type="email"]',
              );
              if (
                emailInput instanceof HTMLInputElement &&
                !emailInput.placeholder
              ) {
                emailInput.placeholder = 'Ex.: maria.silva@printi.com.br';
              }
            };

            // Tenta definir o placeholder várias vezes nos primeiros segundos
            setPlaceholder();
            setTimeout(setPlaceholder, 100);
            setTimeout(setPlaceholder, 500);
            setTimeout(setPlaceholder, 1000);

            // Observer como fallback
            const formContainer = document.getElementById(
              'hubspot-form-container',
            );
            if (formContainer) {
              const observer = new MutationObserver(setPlaceholder);
              observer.observe(formContainer, {
                childList: true,
                subtree: true,
              });
            }
          },
          onFormSubmitted: () => {
            setToast({
              opened: true,
              description: 'E-mail cadastrado com sucesso!',
              type: ToastType.Positive,
            });

            const formContainer = document.getElementById(
              'hubspot-form-container',
            );
            if (formContainer) {
              formContainer.classList.add('form-submitted');
            }
          },
        };

        hubspot.forms.create(config);
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <NewsletterSection>
        <NewsletterGrid>
          <div className="newsletter-image">
            <FsHeading tag={HeadingTag.H2}>Cadastre-se e aproveite</FsHeading>
            <Image
              src={registerImage.src}
              alt="Printi - A sua gráfica, sempre"
              width={680}
              height={428}
              style={{
                maxWidth: '100%',
                height: '485px',
                width: 'auto',
                marginTop: 'var(--spacing-4x)',
                objectFit: 'cover',
              }}
            />
          </div>
          <NewsletterForm className="newsletter-form">
            <HubspotFormContainer id="hubspot-form-container" />
            <FsParagraph className="privacy-policy">
              A Printi precisa das informações de contato que você nos fornece
              para entrar em contato. Você pode deixar de receber essas
              comunicações quando quiser. Mais informações em{' '}
              <Link
                href="https://www.printi.com.br/politica-de-privacidade"
                target="_blank"
                rel="noopener noreferrer"
              >
                Política de Privacidade
              </Link>
              .
            </FsParagraph>
          </NewsletterForm>
        </NewsletterGrid>
      </NewsletterSection>
      <FsToast
        opened={toast.opened}
        type={toast.type}
        onButtonClick={() => setToast((prev) => ({ ...prev, opened: false }))}
      >
        {toast.description}
      </FsToast>
    </>
  );
}
